import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import type {
  OgcOnboardingMode,
  ResourceServerId,
} from '../dataset-home/dataset-home.models';
import { ResourceServerSummaryComponent } from '../resource-server-summary/resource-server-summary.component';
import { BasicInfoComponent } from '../ngsild/basic-info/basic-info.component';
import { MetadataInfoComponent } from '../ngsild/metadata-info/metadata-info.component';
import { NgsildItemService } from '../ngsild/ngsild-item.service';
import { CatalogueApiService } from '@angular-workspace/services';
import {
  getApiErrorDetail,
  getApiErrorStatus,
} from '../ngsild/ngsild-api-error.util';
import type {
  NgsildBasicInfo,
  NgsildMetadataInfo,
  NgsildOnboardingResult,
} from '../ngsild/ngsild.models';
import { clearDataDescriptorDraft } from '../ngsild/data-descriptor.logic';
import { MetadataJsonUploadComponent } from './metadata-json-upload.component';
import { parseDatasetMetadataToOnboarding } from './dataset-metadata-template.util';
import { parseTemplateAdditionalInfo } from '../../shared/additional-info/additional-info.logic';
import { StacCollectionOnboardingComponent } from '../ogc/stac-collection-onboarding/stac-collection-onboarding.component';
import {
  isOgcRasterOnboarding,
  maxWizardStep,
  OGC_RASTER_STAC_STEPS,
  parseWizardStepParam,
} from '../ogc/ogc-raster-wizard.util';

const STEP_QUERY_KEY = 'step';

interface JsonMetadataStep {
  key: string;
  label: string;
}

const BASE_STEPS: JsonMetadataStep[] = [
  { key: 'upload', label: 'Upload metadata' },
  { key: 'preview', label: 'Preview or edit metadata' },
];

@Component({
  selector: 'lib-metadata-json-home',
  standalone: true,
  imports: [
    CommonModule,
    MetadataJsonUploadComponent,
    BasicInfoComponent,
    MetadataInfoComponent,
    ResourceServerSummaryComponent,
    StacCollectionOnboardingComponent,
  ],
  templateUrl: './metadata-json-home.component.html',
  styleUrl: './metadata-json-home.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MetadataJsonHomeComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngsildItemService = inject(NgsildItemService);
  private readonly catalogueApi = inject(CatalogueApiService);

  readonly uploadRef = viewChild(MetadataJsonUploadComponent);
  readonly basicInfoRef = viewChild(BasicInfoComponent);
  readonly metadataInfoRef = viewChild(MetadataInfoComponent);
  readonly stacRef = viewChild(StacCollectionOnboardingComponent);

  readonly resourceServer = input<ResourceServerId>('ngsi-ld');
  readonly ogcOnboardingMode = input<OgcOnboardingMode | null | undefined>(null);
  readonly publisherRole = input<'org-admin' | 'provider'>('provider');

  readonly back = output<void>();
  readonly complete = output<NgsildOnboardingResult>();
  readonly submitError = output<string>();

  readonly isOgcRaster = computed(() =>
    isOgcRasterOnboarding(this.resourceServer(), this.ogcOnboardingMode()),
  );

  readonly isOgcFeature = computed(
    () => this.resourceServer() === 'ogc' && this.ogcOnboardingMode() === 'feature',
  );

  readonly steps = computed(() => {
    if (!this.isOgcRaster()) {
      return BASE_STEPS;
    }
    return [...BASE_STEPS, ...OGC_RASTER_STAC_STEPS];
  });

  readonly currentStep = signal(0);
  readonly basicValid = signal(false);
  readonly metadataValid = signal(false);
  readonly isSubmitting = signal(false);
  readonly previewBasicInfo = signal<NgsildBasicInfo | null>(null);
  readonly createdItemId = signal<string | null>(null);
  readonly stacCollectionId = signal<string | null>(null);
  readonly createdDatasetResult = signal<NgsildOnboardingResult | null>(null);

  readonly canGoNext = computed(() => {
    if (this.isSubmitting()) {
      return false;
    }
    const step = this.currentStep();
    if (step === 0) {
      return !!this.uploadRef()?.hasValidUpload();
    }
    if (step === 1) {
      return this.basicValid() && this.metadataValid();
    }
    if (step === 2) {
      const stac = this.stacRef();
      return !!stac && stac.canSubmitCollection() && !stac.submitting();
    }
    if (step === 3) {
      const stac = this.stacRef();
      return !!stac && stac.canSubmitItem() && !stac.itemSubmitting();
    }
    return false;
  });

  readonly isLastStep = computed(
    () => this.currentStep() === this.steps().length - 1,
  );

  ngOnInit(): void {
    const maxStep = maxWizardStep(this.isOgcRaster());
    const raw = this.route.snapshot.queryParamMap.get(STEP_QUERY_KEY);
    const step = parseWizardStepParam(raw, maxStep);
    this.currentStep.set(step);
    if (step === 1) {
      queueMicrotask(() => this.applyPreviewFromUpload());
    }

    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const next = parseWizardStepParam(
          params.get(STEP_QUERY_KEY),
          maxWizardStep(this.isOgcRaster()),
        );
        if (this.currentStep() !== next) {
          this.currentStep.set(next);
          if (next === 1) {
            queueMicrotask(() => this.applyPreviewFromUpload());
          }
        }
      });
  }

  isStepActive(index: number): boolean {
    return this.currentStep() === index;
  }

  isStepCompleted(index: number): boolean {
    return index < this.currentStep();
  }

  isStepClickable(index: number): boolean {
    if (index === 0) {
      return true;
    }
    if (index === 1) {
      return !!this.uploadRef()?.hasValidUpload();
    }
    if (index <= this.currentStep()) {
      return true;
    }
    if (!this.isOgcRaster()) {
      return false;
    }
    if (index === 2) {
      return !!this.createdItemId();
    }
    if (index === 3) {
      return !!this.stacCollectionId();
    }
    return false;
  }

  onStepClick(index: number): void {
    if (!this.isStepClickable(index)) {
      return;
    }
    if (index === 1 && !this.uploadRef()?.hasValidUpload()) {
      return;
    }
    this.currentStep.set(index);
    this.syncStepQueryParam();
    if (index === 1) {
      queueMicrotask(() => this.applyPreviewFromUpload());
    }
  }

  onBasicValidChange(valid: boolean): void {
    this.basicValid.set(valid);
  }

  onBasicValueChange(value: NgsildBasicInfo): void {
    this.previewBasicInfo.set(value);
  }

  onMetadataValidChange(valid: boolean): void {
    this.metadataValid.set(valid);
  }

  nextButtonLabel(): string {
    if (this.isSubmitting()) {
      if (this.currentStep() === 1 && this.isOgcRaster()) {
        return 'Creating dataset…';
      }
      return 'Creating dataset…';
    }

    const step = this.currentStep();
    if (step === 0) {
      return 'Next →';
    }

    if (this.isOgcRaster()) {
      if (step === 1) {
        return 'Next →';
      }
      if (step === 2) {
        const stac = this.stacRef();
        return stac?.submitting() ? 'Creating...' : 'Create Collection';
      }
      if (step === 3) {
        const stac = this.stacRef();
        if (stac?.itemSubmitting()) {
          return 'Creating...';
        }
        return 'Create STAC Item';
      }
    }

    return 'Create Dataset';
  }

  onBackClick(): void {
    if (this.currentStep() > 0) {
      this.currentStep.update((s) => s - 1);
      this.syncStepQueryParam();
      return;
    }
    this.back.emit();
  }

  async onNextClick(): Promise<void> {
    const step = this.currentStep();

    if (step === 0) {
      if (!this.uploadRef()?.hasValidUpload()) {
        return;
      }
      this.currentStep.set(1);
      this.syncStepQueryParam();
      queueMicrotask(() => this.applyPreviewFromUpload());
      return;
    }

    if (step === 1) {
      this.basicInfoRef()?.form.markAllAsTouched();
      this.metadataInfoRef()?.form.markAllAsTouched();
      if (!this.canGoNext()) {
        return;
      }
      if (!this.metadataInfoRef()?.validateAdditionalInfo()) {
        return;
      }

      if (this.isOgcRaster()) {
        if (!this.createdItemId()) {
          await this.createDatasetItem();
          if (!this.createdItemId()) {
            return;
          }
        }
        this.currentStep.set(2);
        this.syncStepQueryParam();
        return;
      }

      await this.submitAndComplete();
      return;
    }

    if (step === 2 && this.isOgcRaster()) {
      await this.stacRef()?.submitCollection();
      return;
    }

    if (step === 3 && this.isOgcRaster()) {
      await this.stacRef()?.submitItem();
    }
  }

  onStacCollectionStepComplete(event: { id: string }): void {
    this.stacCollectionId.set(event.id);
    this.currentStep.set(3);
    this.syncStepQueryParam();
  }

  onStacItemStepComplete(event: { collectionId: string; itemId: string }): void {
    const result = this.createdDatasetResult();
    if (!result) {
      return;
    }
    clearDataDescriptorDraft();
    this.resetWizard();
    this.complete.emit({
      ...result,
      stacCollectionId: event.collectionId,
      stacItemId: event.itemId,
    });
  }

  onStacOnboardingError(message: string): void {
    this.submitError.emit(message);
  }

  private applyPreviewFromUpload(): void {
    const json = this.uploadRef()?.getParsedJson();
    if (!json) {
      return;
    }
    try {
      const payload = parseDatasetMetadataToOnboarding(json);
      this.basicInfoRef()?.applyValue(payload.basic);
      this.previewBasicInfo.set(payload.basic);
      void this.metadataInfoRef()?.applyValue(payload.metadata);
      this.metadataInfoRef()?.applyAdditionalInfo(parseTemplateAdditionalInfo(json));
    } catch (error) {
      console.error('Failed to map JSON to preview forms:', error);
    }
  }

  private buildBasicPayload(): NgsildBasicInfo | null {
    const ref = this.basicInfoRef();
    if (ref) {
      return ref.form.getRawValue() as NgsildBasicInfo;
    }
    return this.previewBasicInfo();
  }

  private buildMetadataPayload(): NgsildMetadataInfo | null {
    const ref = this.metadataInfoRef();
    if (ref) {
      const raw = ref.form.getRawValue() as NgsildMetadataInfo;
      return {
        ...raw,
        dataDescriptors: ref.getDescriptorsData(),
        dataSample: ref.getSampleData(),
      };
    }
    return null;
  }

  private async createDatasetItem(): Promise<void> {
    const basic = this.buildBasicPayload();
    const metadata = this.buildMetadataPayload();
    if (!basic || !metadata) {
      return;
    }

    this.isSubmitting.set(true);
    try {
      const result = await this.ngsildItemService.createDatasetItem(
        { basic, metadata },
        this.resourceServer(),
        this.ogcOnboardingMode() ?? null,
      );
      this.createdItemId.set(result.itemId);
      this.createdDatasetResult.set({
        payload: result.payload,
        itemId: result.itemId,
        resourceServerId: result.resourceServerId,
        resourceServerKeys: result.resourceServerKeys,
      });
      await this.submitAdditionalInfo(result.itemId);
    } catch (error) {
      console.error('Failed to create dataset from metadata JSON:', error);
      const status = getApiErrorStatus(error);
      const detail = getApiErrorDetail(
        error,
        status === 409
          ? 'Item with this name already exists in local catalogue'
          : 'Failed to create dataset. Please try again.',
      );
      this.submitError.emit(detail);
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private async submitAndComplete(): Promise<void> {
    const basic = this.buildBasicPayload();
    const metadata = this.buildMetadataPayload();
    if (!basic || !metadata) {
      return;
    }

    this.isSubmitting.set(true);
    try {
      const result = await this.ngsildItemService.createDatasetItem(
        { basic, metadata },
        this.resourceServer(),
        this.ogcOnboardingMode() ?? null,
      );
      await this.submitAdditionalInfo(result.itemId);
      clearDataDescriptorDraft();
      this.resetWizard();
      this.complete.emit({
        payload: result.payload,
        itemId: result.itemId,
        resourceServerId: result.resourceServerId,
        resourceServerKeys: result.resourceServerKeys,
      });
    } catch (error) {
      console.error('Failed to create dataset from metadata JSON:', error);
      const status = getApiErrorStatus(error);
      const detail = getApiErrorDetail(
        error,
        status === 409
          ? 'Item with this name already exists in local catalogue'
          : 'Failed to create dataset. Please try again.',
      );
      this.submitError.emit(detail);
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private async submitAdditionalInfo(assetId: string): Promise<void> {
    const items = this.metadataInfoRef()?.getAdditionalInfoItems() ?? [];
    for (const item of items) {
      try {
        await this.catalogueApi.postProviderFeedback({
          assetId,
          type: item.type,
          data: item.data,
        });
      } catch (error) {
        console.error('Failed to submit additional info item:', error);
      }
    }
  }

  private resetWizard(): void {
    this.basicInfoRef()?.resetForm();
    this.metadataInfoRef()?.resetForm();
    this.uploadRef()?.removeFile();
    this.basicValid.set(false);
    this.metadataValid.set(false);
    this.previewBasicInfo.set(null);
    this.createdItemId.set(null);
    this.stacCollectionId.set(null);
    this.createdDatasetResult.set(null);
    this.currentStep.set(0);
    this.syncStepQueryParam();
  }

  private syncStepQueryParam(): void {
    const step = this.currentStep();
    const next = String(step);
    if (this.route.snapshot.queryParamMap.get(STEP_QUERY_KEY) === next) {
      return;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [STEP_QUERY_KEY]: step },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
